import { View, Text, StyleSheet, TouchableOpacity, Linking } from "react-native";
import { Image } from "expo-image";
import { useColors, ThemeColors } from "../hooks/useColors";
import { spacing, typography } from "../theme";
import { trackEvent } from "../services/posthog";

interface AppDownloadBannerProps {
  style?: any;
  onDismiss?: () => void;
  compact?: boolean;
}

export function AppDownloadBanner({ style, onDismiss, compact = false }: AppDownloadBannerProps) {
  const colors = useColors();
  const styles = createStyles(colors);

  const handleLinkPress = async (platform: "ios" | "android", url: string) => {
    // Track link click
    trackEvent("app_download_link_clicked", {
      platform,
      source: "auth_screen",
      url,
    });
    
    try {
      const canOpen = await Linking.canOpenURL(url);
      if (canOpen) {
        await Linking.openURL(url);
      } else {
        console.warn(`Cannot open URL: ${url}`);
      }
    } catch (error) {
      console.error(`Error opening URL ${url}:`, error);
    }
  };

  return (
    <View style={[styles.container, compact && styles.containerCompact, style]}>
      <View style={[styles.banner, compact && styles.bannerCompact]}>
        <Text style={[styles.title, compact && styles.titleCompact]}>
          Get the mobile app
        </Text>
        <View style={[styles.buttonContainer, compact && styles.buttonContainerCompact]}>
          <TouchableOpacity
            onPress={() =>
              handleLinkPress(
                "ios",
                "https://apps.apple.com/us/app/jadetrade/id6746063023"
              )
            }
            activeOpacity={0.7}
          >
            <Image 
              source={require("../assets/app-store-badge.svg")}
              style={[styles.badgeImage, compact && styles.badgeImageCompact]}
              contentFit="contain"
            />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() =>
              handleLinkPress(
                "android",
                "https://play.google.com/store/apps/details?id=com.jadetrade"
              )
            }
            activeOpacity={0.7}
          >
            <Image 
              source={require("../assets/google-play-badge.svg")}
              style={[styles.badgeImage, styles.googleBadge, compact && styles.badgeImageCompact, compact && styles.googleBadgeCompact]}
              contentFit="contain"
            />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      marginBottom: spacing.md,
      alignItems: "center",
    },
    containerCompact: {
      marginBottom: spacing.xs,
    },
    banner: {
      backgroundColor: colors.primary,
      borderRadius: 16,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      alignItems: "center",
    },
    bannerCompact: {
      paddingVertical: spacing.xs,
      paddingHorizontal: spacing.sm,
      borderRadius: 10,
    },
    title: {
      ...typography.body,
      fontWeight: "700",
      color: "#000000",
      marginBottom: spacing.sm,
      textAlign: "center",
      fontSize: 16,
    },
    titleCompact: {
      fontSize: 11,
      marginBottom: 4,
    },
    buttonContainer: {
      flexDirection: "row",
      gap: spacing.sm,
      justifyContent: "center",
      alignItems: "center",
    },
    buttonContainerCompact: {
      gap: 6,
    },
    badgeImage: {
      width: 120,
      height: 40,
    },
    badgeImageCompact: {
      width: 85,
      height: 28,
    },
    googleBadge: {
      // Google badge has different aspect ratio (238.96 x 70.87), adjust width
      width: 135,
    },
    googleBadgeCompact: {
      width: 95,
    },
  });


